import { useState, useEffect } from 'react';
import { RefreshCw, TrendingUp, Users, BarChart3, MapPin } from 'lucide-react';
import apiServices from '../../services/api';
import type { GeneralManagerStats } from '../../types/api';

import { ScoreTrendChart } from '../charts/ScoreTrendChart';
import { ZoneComparisonChart } from '../charts/ZoneComparisonChart';
import { TopStoresChart } from '../charts/TopStoresChart';
export const GeneralManagerDashboard: React.FC = () => {
  const [stats, setStats] = useState<GeneralManagerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiServices.admin.getGeneralManagerStats();
      setStats(data);
    } catch (err) {
      console.error('Error loading general manager stats:', err);
      setError('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
        {error || 'No se pudieron cargar las estadísticas'}
      </div>
    );
  }

  const { overview, scoreTrend, zonePerformance, topStores } = stats;
  const trend = overview.reportsThisMonth - overview.reportsLastMonth;
  const trendPercentage =
    overview.reportsLastMonth > 0
      ? ((trend / overview.reportsLastMonth) * 100).toFixed(1)
      : '0';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            Dashboard Gerente General
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Vista general del rendimiento del sistema
          </p>
        </div>
        <button
          onClick={loadStats}
          disabled={loading}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-primary-dark text-white rounded-lg hover:bg-primary-accent transition-colors disabled:opacity-50 text-sm sm:text-base"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
          </div>
          <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
            {overview.totalReports}
          </div>
          <div className="text-xs sm:text-sm text-gray-600">
            Total de Reportes
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
          </div>
          <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
            {overview.avgScore.toFixed(1)}%
          </div>
          <div className="text-xs sm:text-sm text-gray-600">
            Promedio General
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
          </div>
          <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
            {overview.reportsThisMonth}
          </div>
          <div className="text-xs sm:text-sm text-gray-600">
            Reportes Este Mes
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <MapPin className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" />
          </div>
          <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
            <span className={trend >= 0 ? 'text-green-600' : 'text-red-600'}>
              {trend >= 0 ? '+' : ''}
              {trendPercentage}%
            </span>
          </div>
          <div className="text-xs sm:text-sm text-gray-600">
            vs Mes Anterior
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <ScoreTrendChart data={scoreTrend} />
        <ZoneComparisonChart data={zonePerformance} />
      </div>

      {/* Top Stores */}
      <TopStoresChart data={topStores} />
    </div>
  );
};
